import { createContext, useContext } from 'react';
import {
  AnalyticsManager,
  OneHubEventBuilder,
} from '@tribal-worldwide-london/fa-analytics';
import { ConfigManager } from '@tribal-worldwide-london/fa-config';
import ContentManager from '@tribal-worldwide-london/fa-content';
import { FeatureappServiceV1 } from '@volkswagen-onehub/featureapp-service';
import {
  RetailerService,
  VwgRetailerService,
} from '@tribal-worldwide-london/fa-retailer-service-vwg';
import {
  PdbService,
  VwgPdbService,
} from '@tribal-worldwide-london/fa-pdb-service-vwg';
import { ProductService } from '@tribal-worldwide-london/fa-product-service-vwg';
import {
  ConfigurationService,
  VWGConfigurationService,
} from '@tribal-worldwide-london/fa-configuration-service-vwg';
import {
  StockService,
  VWGStockService,
} from '@tribal-worldwide-london/fa-stock-service-vwg';
import {
  ImageService,
  VwgImageService,
} from '@tribal-worldwide-london/fa-image-service-vwg';
import { AnalyticsDriverType, AnalyticsDriverTypes } from '@tribal-worldwide-london/fa-analytics';
import { VwgProductService } from '@tribal-worldwide-london/fa-product-service-vwg';
import globalValues from '../analytics/globalValues';
import {
  AgentService,
  VWGAgentService,
} from '@tribal-worldwide-london/fa-agent-service-vwg';
import {
  ProductFinanceService,
  VwgProductFinanceService,
} from '@tribal-worldwide-london/fa-product-finance-service-vwg';
import {
  FinanceService,
  VwgFinanceService,
} from '@tribal-worldwide-london/fa-finance-service-vwg';
import { CarService, VwgCarService } from '@tribal-worldwide-london/fa-car-service-vwg';
import {
  StockService as StockDataService,
  VWGStockService as VWGStockDataService,
} from '@tribal-worldwide-london/fa-stock-data-service-vwg';


export type TribalContextType = {
  analyticsManager: AnalyticsManager;
  analyticsBuilder: OneHubEventBuilder;
  configManager: ConfigManager;
  contentManager: ContentManager;
  retailerService: VwgRetailerService;
  pdbService: VwgPdbService;
  productService: VwgProductService;
  productFinanceService: VwgProductFinanceService;
  financeService: VwgFinanceService;
  carService: VwgCarService;
  configurationService: VWGConfigurationService;
  imageService: VwgImageService;
  stockService: VWGStockService;
  agentService: VWGAgentService;
  appConfig: AppConfig;
  stockDataService: VWGStockDataService;
};

export type AppConfig = {
  pdbId?: string;
};

type AnalyticsProvider = {
  providerId: AnalyticsDriverTypes;
  configuration: object;
};

const TribalContext = createContext<TribalContextType>({} as TribalContextType);

export function useTribalContext() {
  return useContext(TribalContext);
}

export function getTribalContextManagers(
  config: object,
  localConfig: object,
  content: object,
  localContent: object,
  featureAppService: FeatureappServiceV1 | undefined,
  appConfig: AppConfig
) {
  // Initialise Config Manager.
  const configManager = new ConfigManager(
    {
      provider: import.meta.env.VITE_APP_CONFIG_PROVIDER,
      config,
    },
    localConfig
  );

  // Initialise Content Manager.
  const contentProviderId = configManager.get('contentProvider.providerId');
  const contentManager =
    contentProviderId.toLowerCase() === 'json'
      ? new ContentManager('Json', { content: localContent })
      : new ContentManager(contentProviderId, {
          content,
        });

  const analyticsProviders: Array<AnalyticsProvider> =
    configManager.get('analyticsProviders');
  const analyticsDrivers: Array<AnalyticsDriverType> = [];
  analyticsProviders.forEach(
    ({ providerId, configuration }: AnalyticsProvider) => {
      let config: any = {};
      if (providerId === 'onehub') {
        config = {
          ...configuration,
          featureAppService,
        };
      } else {
        config = configuration;
      }
      const driver: AnalyticsDriverType = {
        provider: providerId,
        config,
      };
      analyticsDrivers.push(driver);
    }
  );

  // Initialise Analytics Manager
  const analyticsManager = new AnalyticsManager(analyticsDrivers);

  const globalAnalyticsValues = globalValues(configManager, appConfig);

  // Initialise Analytics Event Builder
  const analyticsBuilder = new OneHubEventBuilder(globalAnalyticsValues);

  //Checks if a base URI exists in .env (designed to clear value for mock responses in dev mode)
  const getApiBaseUrl = (key: string) => {
    const baseUri = import.meta.env.VITE_MOCK_SERVER_BASE_URI;
    return typeof baseUri === 'string' ? baseUri : configManager.get(key);
  };

  const retailerService = new RetailerService(getApiBaseUrl('apis.retailer'));

  const pdbService = new PdbService(getApiBaseUrl('apis.pdb'));

  const productService = new ProductService(
    getApiBaseUrl('apis.product'),
    'PC'
  );

  const configurationService = new ConfigurationService(
    getApiBaseUrl('apis.configuration')
  );

  const stockService = new StockService(getApiBaseUrl('apis.stock'));

  const imageService = new ImageService(configManager.get('apis.image'));

  const agentService = new AgentService(getApiBaseUrl('apis.agent'));

  const productFinanceService = new ProductFinanceService(
    configManager.get('apis.productFinance')
  );

  const financeService = new FinanceService(configManager.get('apis.finance'));

  const carService = new CarService(configManager.get('apis.car'));


  const stockDataService = new StockDataService(
    getApiBaseUrl('apis.vtpStock')
  );

  return {
    configManager,
    contentManager,
    analyticsManager,
    analyticsBuilder,
    retailerService,
    pdbService,
    productService,
    productFinanceService,
    financeService,
    carService,
    configurationService,
    stockService,
    imageService,
    agentService,
    appConfig,
    stockDataService,
  };
}

export default TribalContext;
